import React, { useState } from "react";
import SafeIcon from "../common/SafeIcon";
import VarietySelector from "./VarietySelector";
import CustomerSelector from "./CustomerSelector";
import * as FiIcons from "react-icons/fi";
import {
  format,
  addDays,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  isWithinInterval,
  differenceInCalendarDays,
} from "date-fns";

const {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiPlusCircle,
  FiMinusCircle,
  FiX,
  FiActivity,
  FiRepeat,
  FiZap,
  FiImage,
  FiUser,
  FiCheckCircle,
  FiAlertCircle,
  FiSearch,
  FiFilter,
} = FiIcons;

const OrderManager = ({
  orders,
  onUpdate,
  varieties,
  batches,
  setBatches,
  customers,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All"); // 'All', 'No sync', 'Ready to sync', 'Done Sync'
  const [dateFilter, setDateFilter] = useState("All"); // 'All', 'This Week', 'Next Week', 'This Month', 'Next Month'
  const [selectedOrders, setSelectedOrders] = useState([]);

  const toggleOrder = (orderId) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleBatchDelete = async () => {
    if (!window.confirm(`Delete ${selectedOrders.length} selected orders?`))
      return;
    setIsLoading(true);
    try {
      await Promise.all(
        selectedOrders.map((id) =>
          fetch(`/api/orders/${id}`, { method: "DELETE" })
        )
      );
      setSelectedOrders([]);
      if (onUpdate) onUpdate();
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    customer: "",
    dateType: "delivery",
    baseDate: format(new Date(), "yyyy-MM-dd"),
    isSudden: false,
    items: [{ varietyId: "", quantity: "", bufferQuantity: "" }],
    notes: "",
    syncStatus: "No sync",
    priceType: "Standard",
    deliveryFee: "",
  });

  // Filter Orders Logic
  const filteredOrders = orders.filter((order) => {
    // Exclude manual sowing tasks (Stock (Manual))
    if (order.customer === "Stock (Manual)") return false;

    // Search Filter
    const matchesSearch =
      order.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id.toString().includes(searchTerm);

    // Status Filter
    const matchesStatus =
      statusFilter === "All" ||
      (order.syncStatus || "No sync") === statusFilter;

    // Date Filter
    let matchesDate = true;
    if (dateFilter !== "All") {
      const orderDate = new Date(order.baseDate);
      const today = new Date();

      if (dateFilter === "This Week") {
        matchesDate = isWithinInterval(orderDate, {
          start: startOfWeek(today),
          end: endOfWeek(today),
        });
      } else if (dateFilter === "Next Week") {
        const nextWeekStart = addDays(endOfWeek(today), 1);
        const nextWeekEnd = addDays(nextWeekStart, 6);
        matchesDate = isWithinInterval(orderDate, {
          start: nextWeekStart,
          end: nextWeekEnd,
        });
      } else if (dateFilter === "This Month") {
        matchesDate = isWithinInterval(orderDate, {
          start: startOfMonth(today),
          end: endOfMonth(today),
        });
      } else if (dateFilter === "Next Month") {
        const nextMonthStart = startOfMonth(addDays(endOfMonth(today), 1));
        const nextMonthEnd = endOfMonth(nextMonthStart);
        matchesDate = isWithinInterval(orderDate, {
          start: nextMonthStart,
          end: nextMonthEnd,
        });
      }
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  // Calculate available stock per variety from ALL previous orders
  const calculateStock = (varietyId) => {
    if (!varietyId) return 0;
    const vId = parseInt(varietyId);

    const today = new Date();

    // Total Buffer Produced (Last 7 Days)
    const totalBuffer = orders.reduce((acc, order) => {
      if (order.isSudden) return acc;
      const item = order.items.find(
        (i) => i.varietyId === vId || i.variety?.id === vId
      );
      if (!item || !item.bufferQuantity) return acc;

      const v = varieties.find((va) => va.id === vId);
      if (!v) return acc;

      const base = new Date(order.baseDate);
      let harvestDate = base;
      if (order.dateType === "sowing") {
        const days = (v.growingDays || 0) + (v.darkTreatmentDays || 0);
        harvestDate = addDays(base, days);
      }

      const diff = differenceInCalendarDays(today, harvestDate);
      if (diff >= 0 && diff <= 7) {
        return acc + (parseFloat(item.bufferQuantity) || 0);
      }
      return acc;
    }, 0);

    // Total Buffer Consumed (Last 7 Days)
    const totalConsumed = orders.reduce((acc, order) => {
      if (!order.isSudden) return acc;
      const item = order.items.find(
        (i) => i.varietyId === vId || i.variety?.id === vId
      );
      if (!item) return acc;

      const orderDate = new Date(order.baseDate);
      const diff = differenceInCalendarDays(today, orderDate);

      if (diff >= 0 && diff <= 7) {
        return acc + (parseFloat(item.quantity) || 0);
      }
      return acc;
    }, 0);

    return Math.max(0, totalBuffer - totalConsumed);
  };

  const handleAddItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { varietyId: "", quantity: "", bufferQuantity: "" },
      ],
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData((prev) => ({ ...prev, items: newItems }));
  };

  const calculateItemDates = (varietyId, baseDate, dateType, isSudden) => {
    const variety = varieties.find((v) => v.id === parseInt(varietyId));
    if (!variety || !baseDate) return null;

    if (isSudden) {
      // Sudden orders are immediate, no sowing date needed (already grown)
      return {
        sowingDate: null,
        deliveryDate: new Date(baseDate),
        harvestDate: new Date(baseDate),
        variety,
      };
    }

    const totalCycle =
      (variety.growingDays || 0) + (variety.darkTreatmentDays || 0);
    const date = new Date(baseDate);
    let sowingDate, deliveryDate;

    if (dateType === "delivery") {
      deliveryDate = date;
      sowingDate = subDays(date, totalCycle);
    } else {
      sowingDate = date;
      deliveryDate = addDays(date, totalCycle);
    }

    return { sowingDate, deliveryDate, harvestDate: deliveryDate, variety };
  };

  const handleMoveItem = (index, direction) => {
    const newItems = [...formData.items];
    if (direction === "up" && index > 0) {
      [newItems[index], newItems[index - 1]] = [
        newItems[index - 1],
        newItems[index],
      ];
    } else if (direction === "down" && index < newItems.length - 1) {
      [newItems[index], newItems[index + 1]] = [
        newItems[index + 1],
        newItems[index],
      ];
    }
    setFormData((prev) => ({ ...prev, items: newItems }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // 1. If Editing, handle as normal (can't easily convert to recurring rule pattern yet without more UI)
      if (editingOrder) {
        const response = await fetch(`/api/orders/${editingOrder.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
        if (response.ok) {
          if (onUpdate) onUpdate();
          resetForm();
        }
      } else {
        // 3. Normal Single Order
        const response = await fetch("/api/orders", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          if (onUpdate) onUpdate();
          resetForm();
        }
      }
    } catch (error) {
      console.error("Error saving order:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;
    try {
      const response = await fetch(`/api/orders/${id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error("Error deleting order:", error);
    }
  };

  const resetForm = () => {
    setIsModalOpen(false);
    setEditingOrder(null);
    setFormData({
      customer: "",
      dateType: "delivery",
      baseDate: format(new Date(), "yyyy-MM-dd"),
      isSudden: false,
      items: [{ varietyId: "", quantity: "", bufferQuantity: "0" }],
      notes: "",
      syncStatus: "No sync",
      priceType: "Standard",
    });
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      customer: order.customer,
      dateType: order.dateType,
      baseDate: format(new Date(order.baseDate), "yyyy-MM-dd"),
      isSudden: order.isSudden || false,
      items: order.items.map((item) => ({
        varietyId: (item.varietyId || item.variety?.id || "").toString(),
        quantity: item.quantity.toString(),
        bufferQuantity: (item.bufferQuantity || 0).toString(),
      })),
      notes: order.notes || "",
      syncStatus: order.syncStatus || "No sync",
      priceType: order.priceType || "Standard",
      deliveryFee: order.deliveryFee || "",
    });
    setIsModalOpen(true);
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto pb-24 md:pb-8 space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] tracking-tight">
            Order Management
          </h1>
          <p className="text-gray-500 mt-2 font-light">
            Plan production & manage customer orders
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-[#16a34a] hover:bg-green-700 text-white px-8 py-3 rounded-lg flex items-center space-x-2 transition-all shadow-md shadow-green-100 font-medium"
        >
          <SafeIcon icon={FiPlus} />
          <span>New Order</span>
        </button>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        {/* Filter Tabs (Left) */}
        <div className="flex items-center space-x-1 bg-white p-1.5 rounded-xl shadow-sm border border-gray-100">
          {["All", "Ready to sync", "No sync", "Done Sync"].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${statusFilter === status
                ? "bg-[#16a34a] text-white shadow-md shadow-green-100"
                : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                }`}
            >
              {status}
            </button>
          ))}
        </div>

        {/* Date Filter (Middle) */}
        <div className="relative">
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="appearance-none bg-white border border-gray-200 text-gray-700 py-3 pl-4 pr-10 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] font-light shadow-sm cursor-pointer"
          >
            <option value="All">All Dates</option>
            <option value="This Week">This Week</option>
            <option value="Next Week">Next Week</option>
            <option value="This Month">This Month</option>
            <option value="Next Month">Next Month</option>
          </select>
          <SafeIcon
            icon={FiIcons.FiCalendar}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
          />
        </div>

        {/* Search Bar (Right) */}
        <div className="relative w-full md:w-80">
          <SafeIcon
            icon={FiSearch}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] transition-all font-light shadow-sm"
          />
        </div>
      </div>

      {
        /* Batch Actions */
        selectedOrders.length > 0 && (
          <div className="flex items-center justify-between mb-4 bg-gray-900 text-white p-4 rounded-xl shadow-lg animate-fade-in-up">
            <span className="font-medium pl-2">
              {selectedOrders.length} orders selected
            </span>
            <button
              onClick={handleBatchDelete}
              className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-bold transition-all"
            >
              <SafeIcon icon={FiTrash2} />
              <span>Delete Selected</span>
            </button>
          </div>
        )
      }

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50/50 border-b border-gray-100">
            <tr>
              <th className="px-8 py-5 text-left w-16">
                <input
                  type="checkbox"
                  checked={
                    filteredOrders.length > 0 &&
                    selectedOrders.length === filteredOrders.length
                  }
                  onChange={(e) => {
                    if (e.target.checked)
                      setSelectedOrders(filteredOrders.map((o) => o.id));
                    else setSelectedOrders([]);
                  }}
                  className="w-5 h-5 rounded border-gray-300 text-[#1e3a8a] focus:ring-[#1e3a8a] transition-all cursor-pointer"
                />
              </th>
              <th className="px-8 py-5 text-left text-xs font-medium text-gray-400 uppercase tracking-widest">
                ID
              </th>
              <th className="px-8 py-5 text-left text-xs font-medium text-gray-400 uppercase tracking-widest">
                Customer
              </th>
              <th className="px-8 py-5 text-left text-xs font-medium text-gray-400 uppercase tracking-widest">
                Mode
              </th>
              <th className="px-8 py-5 text-left text-xs font-medium text-gray-400 uppercase tracking-widest">
                Items
              </th>
              <th className="px-8 py-5 text-left text-xs font-medium text-gray-400 uppercase tracking-widest">
                Total
              </th>
              <th className="px-8 py-5 text-left text-xs font-medium text-gray-400 uppercase tracking-widest">
                Delivery Date
              </th>
              <th className="px-8 py-5 text-left text-xs font-medium text-gray-400 uppercase tracking-widest">
                Invoice Sync
              </th>
              <th className="px-8 py-5 text-left text-xs font-medium text-gray-400 uppercase tracking-widest">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredOrders.map((order) => (
              <tr
                key={order.id}
                className="hover:bg-gray-50/50 transition-colors"
              >
                <td className="pl-8 py-6 pr-0 w-16">
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order.id)}
                    onChange={() => toggleOrder(order.id)}
                    className="w-5 h-5 rounded border-gray-300 text-[#1e3a8a] focus:ring-[#1e3a8a] transition-all cursor-pointer"
                  />
                </td>
                <td className="px-8 py-6">
                  <span className="text-sm font-medium text-gray-900">
                    #{order.id}
                  </span>
                </td>
                <td className="px-8 py-6">
                  <div className="flex items-center">
                    {(() => {
                      const customerObj = customers.find(
                        (c) => c.name === order.customer
                      );
                      return (
                        <div className="w-10 h-10 rounded-full bg-gray-100 overflow-hidden mr-3 flex-shrink-0 border border-gray-100 flex items-center justify-center">
                          {customerObj && customerObj.image ? (
                            <img
                              src={customerObj.image}
                              alt={order.customer}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <SafeIcon icon={FiUser} className="text-gray-400" />
                          )}
                        </div>
                      );
                    })()}
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {order.customer}
                      </div>
                      {order.isRecurring && (
                        <span className="inline-flex items-center mt-0.5 text-[10px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                          <SafeIcon icon={FiRepeat} className="mr-1" />{" "}
                          Recurring
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-8 py-6">
                  {order.isSudden ? (
                    <span className="inline-flex items-center text-xs font-medium text-orange-600 bg-orange-50 px-2.5 py-1 rounded-full border border-orange-100">
                      <SafeIcon icon={FiZap} className="mr-1" /> Sudden
                    </span>
                  ) : (
                    <div className="flex flex-col items-start space-y-1">
                      <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                        Standard
                      </span>
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">
                        By {order.dateType}
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-8 py-6">
                  <div className="flex flex-col gap-2">
                    {order.items.map((item, idx) => {
                      const v = varieties.find((v) => v.id === item.varietyId);
                      const calc = calculateItemDates(
                        item.varietyId,
                        order.baseDate,
                        order.dateType,
                        order.isSudden
                      );

                      return (
                        <div key={idx} className="flex flex-col space-y-1">
                          <div className="flex items-center space-x-2 bg-white border border-gray-100 pr-3 rounded-lg overflow-hidden w-fit">
                            <div className="w-8 h-8 bg-gray-50 flex items-center justify-center flex-shrink-0">
                              {v && v.image ? (
                                <img
                                  src={v.image}
                                  alt=""
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <SafeIcon
                                  icon={FiImage}
                                  className="text-gray-300 text-xs"
                                />
                              )}
                            </div>
                            <span className="text-sm font-light text-gray-900 py-1">
                              {v?.name || "Unknown"}
                            </span>
                            <span className="text-xs font-medium text-[#16a34a] py-1">
                              {item.quantity}{v?.category === 'Edible Flowers' ? 'g' : 'u'}
                            </span>
                            {item.bufferQuantity > 0 && !order.isSudden && (
                              <span className="text-xs font-medium text-orange-400 py-1">
                                +{item.bufferQuantity}B
                              </span>
                            )}
                          </div>
                          {calc &&
                            calc.sowingDate &&
                            !order.isSudden &&
                            ![
                              "lettuce",
                              "edible flowers",
                              "edible leaves",
                            ].includes((v?.category || "").toLowerCase()) && (
                              <div className="text-[10px] text-gray-400 pl-1">
                                Sow:{" "}
                                <span className="font-medium text-gray-600">
                                  {format(calc.sowingDate, "MMM dd")}
                                </span>
                              </div>
                            )}
                        </div>
                      );
                    })}
                  </div>
                </td>
                <td className="px-8 py-6">
                  <div className="text-sm font-medium text-gray-900">
                    {(() => {
                      const itemsTotal = order.items.reduce((sum, item) => {
                        const v = varieties.find(
                          (v) => v.id === item.varietyId
                        );
                        const price = v
                          ? parseFloat(
                            (order.priceType === "Supplier"
                              ? v.supplierPrice
                              : v.price) || 0
                          )
                          : 0;
                        return sum + price * item.quantity;
                      }, 0);
                      const deliveryFee = parseFloat(order.deliveryFee || 0);
                      return `RM ${(itemsTotal + deliveryFee).toFixed(2)}`;
                    })()}
                  </div>
                </td>
                {/* <td className="px-8 py-6 text-sm font-light text-gray-600">
                  {(() => {
                    if (order.isSudden) return <span className="text-gray-300">-</span>;
                    if (order.dateType === 'sowing') return format(new Date(order.baseDate), 'MMM dd, yyyy');

                    // If Standard & By Delivery, calculate Sowing Date range
                    const sowDates = order.items.map(item => {
                      const v = varieties.find(v => v.id === item.varietyId);
                      if (!v) return null;
                      const days = (v.growingDays || 0) + (v.darkTreatmentDays || 0);
                      return subDays(new Date(order.baseDate), days);
                    }).filter(Boolean).sort((a, b) => a - b);

                    if (sowDates.length === 0) return '-';
                    if (sowDates.length === 1 || sowDates[0].getTime() === sowDates[sowDates.length - 1].getTime()) {
                      return format(sowDates[0], 'MMM dd, yyyy');
                    }
                    return (
                      <div className="flex flex-col text-xs">
                        <span>{format(sowDates[0], 'MMM dd')}</span>
                        <span className="text-gray-400 mx-1">to</span>
                        <span>{format(sowDates[sowDates.length - 1], 'MMM dd, yyyy')}</span>
                      </div>
                    );
                  })()}
                </td> */}
                <td className="px-8 py-6 text-sm font-light text-gray-600">
                  {(() => {
                    if (order.isSudden)
                      return format(new Date(order.baseDate), "MMM dd, yyyy");
                    if (order.dateType === "delivery")
                      return format(new Date(order.baseDate), "MMM dd, yyyy");

                    // If Standard & By Sowing, calculate Delivery Date range
                    const delDates = order.items
                      .map((item) => {
                        const v = varieties.find(
                          (v) => v.id === item.varietyId
                        );
                        if (!v) return null;
                        const days =
                          (v.growingDays || 0) + (v.darkTreatmentDays || 0);
                        return addDays(new Date(order.baseDate), days);
                      })
                      .filter(Boolean)
                      .sort((a, b) => a - b);

                    if (delDates.length === 0) return "-";
                    if (
                      delDates.length === 1 ||
                      delDates[0].getTime() ===
                      delDates[delDates.length - 1].getTime()
                    ) {
                      return format(delDates[0], "MMM dd, yyyy");
                    }
                    return (
                      <div className="flex flex-col text-xs">
                        <span>{format(delDates[0], "MMM dd")}</span>
                        <span className="text-gray-400 mx-1">to</span>
                        <span>
                          {format(
                            delDates[delDates.length - 1],
                            "MMM dd, yyyy"
                          )}
                        </span>
                      </div>
                    );
                  })()}
                </td>
                <td className="px-8 py-6">
                  {(() => {
                    const status = order.syncStatus || "Ready to sync";
                    let color = "bg-blue-50 text-blue-700 border-blue-100";
                    let icon = FiAlertCircle;

                    if (status === "No sync") {
                      color = "bg-gray-100 text-gray-500 border-gray-200";
                      icon = FiX;
                    } else if (status === "Done Sync") {
                      color = "bg-green-50 text-green-700 border-green-100";
                      icon = FiCheckCircle;
                    }

                    return (
                      <span
                        className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full border ${color}`}
                      >
                        <SafeIcon icon={icon} className="mr-1.5 text-xs" />
                        {status}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-8 py-6 text-sm flex space-x-2">
                  <button
                    onClick={() => handleEdit(order)}
                    className="p-2 text-[#1e3a8a] hover:bg-blue-50 rounded-lg transition-all"
                  >
                    <SafeIcon icon={FiEdit2} />
                  </button>
                  <button
                    onClick={() => handleDelete(order.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {filteredOrders.length === 0 && (
          <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            <p className="text-gray-400 text-sm">No orders found.</p>
          </div>
        )}
        {filteredOrders.map((order) => {
          const customerObj = customers.find((c) => c.name === order.customer);
          const status = order.syncStatus || "Ready to sync";
          let statusColor = "bg-blue-50 text-blue-700 border-blue-100";
          let statusIcon = FiAlertCircle;

          if (status === "No sync") {
            statusColor = "bg-gray-100 text-gray-500 border-gray-200";
            statusIcon = FiX;
          } else if (status === "Done Sync") {
            statusColor = "bg-green-50 text-green-700 border-green-100";
            statusIcon = FiCheckCircle;
          }

          const orderTotal = order.items.reduce((sum, item) => {
            const v = varieties.find((v) => v.id === item.varietyId);
            const price = v
              ? parseFloat(
                (order.priceType === "Supplier"
                  ? v.supplierPrice
                  : v.price) || 0
              )
              : 0;
            return sum + price * item.quantity;
          }, 0);

          return (
            <div
              key={order.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 relative overflow-hidden"
            >
              {/* Top Row: ID and Status */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order.id)}
                    onChange={() => toggleOrder(order.id)}
                    className="w-5 h-5 rounded border-gray-300 text-[#1e3a8a] focus:ring-[#1e3a8a] transition-all cursor-pointer"
                  />
                  <span
                    className={`inline-flex items-center text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${statusColor}`}
                  >
                    <SafeIcon icon={statusIcon} className="mr-1 text-[10px]" />{" "}
                    {status}
                  </span>
                </div>
              </div>

              {/* Customer Info */}
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-gray-50 overflow-hidden mr-3 flex-shrink-0 border border-gray-100 flex items-center justify-center">
                  {customerObj && customerObj.image ? (
                    <img
                      src={customerObj.image}
                      alt={order.customer}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <SafeIcon icon={FiUser} className="text-gray-400" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{order.customer}</h3>
                  <div className="flex items-center space-x-2 mt-1">
                    {order.isSudden ? (
                      <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded border border-orange-100">
                        SUDDEN
                      </span>
                    ) : (
                      <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                        STD
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400">
                      {format(new Date(order.baseDate), "MMM dd")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Items List (Compact) */}
              <div className="space-y-2 mb-4 bg-gray-50/50 p-3 rounded-lg border border-gray-50">
                {order.items.map((item, idx) => {
                  const v = varieties.find((v) => v.id === item.varietyId);
                  return (
                    <div
                      key={idx}
                      className="flex justify-between items-center text-sm"
                    >
                      <span className="text-gray-600 truncate flex-1 mr-2">
                        {v?.name}
                      </span>
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        <span className="font-bold text-gray-900">
                          {item.quantity}
                        </span>
                        {item.bufferQuantity > 0 && (
                          <span className="text-[10px] text-orange-500">
                            +{item.bufferQuantity}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer: Price & Actions */}
              <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                <span className="text-lg font-light text-[#1e3a8a]">
                  RM {orderTotal.toFixed(2)}
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(order)}
                    className="p-2 text-[#1e3a8a] bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <SafeIcon icon={FiEdit2} />
                  </button>
                  <button
                    onClick={() => handleDelete(order.id)}
                    className="p-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-[#1e3a8a]/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-100">
            <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
              <h2 className="text-xl font-light text-[#1e3a8a]">
                {editingOrder ? "Edit Order" : "New Order"}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="p-8 overflow-y-auto space-y-8"
            >
              {/* Order Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2 flex items-center space-x-6 mb-2">
                  <label className="flex items-center space-x-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.isSudden}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          isSudden: e.target.checked,
                          dateType: "delivery",
                        }))
                      } // Sudden is always Delivery date
                      className="w-5 h-5 text-orange-500 rounded border-gray-300 focus:ring-orange-500"
                    />
                    <span className="text-sm font-medium text-gray-700 group-hover:text-orange-500 transition-colors flex items-center">
                      <SafeIcon icon={FiZap} className="mr-1" /> Sudden Order
                      (Use Buffer)
                    </span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Name
                  </label>
                  {customers && customers.length > 0 ? (
                    <CustomerSelector
                      customers={customers}
                      value={formData.customer}
                      onChange={(val) =>
                        setFormData((p) => ({ ...p, customer: val }))
                      }
                    />
                  ) : (
                    <input
                      type="text"
                      value={formData.customer}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, customer: e.target.value }))
                      }
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                      placeholder="e.g. Skyline Hotel"
                      required
                    />
                  )}
                </div>

                {!formData.isSudden && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Planning Mode
                    </label>
                    <select
                      value={formData.dateType}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, dateType: e.target.value }))
                      }
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light bg-white"
                    >
                      <option value="delivery">By Delivery Date</option>
                      <option value="sowing">By Sowing Date</option>
                    </select>
                  </div>
                )}

                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price Type
                    </label>
                    <select
                      value={formData.priceType}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, priceType: e.target.value }))
                      }
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light bg-white"
                    >
                      <option value="Standard">Standard Price</option>
                      <option value="Supplier">Supplier Price</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Delivery Fee
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">RM</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.deliveryFee}
                        onChange={(e) => setFormData(p => ({ ...p, deliveryFee: e.target.value }))}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                <div
                  className={
                    formData.isSudden ? "md:col-span-1" : "md:col-span-2"
                  }
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.dateType === "delivery" || formData.isSudden
                      ? "Delivery Date"
                      : "Sowing Date"}
                  </label>
                  <input
                    type="date"
                    value={formData.baseDate}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, baseDate: e.target.value }))
                    }
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                    required
                  />
                </div>
              </div>

              {/* Items Section */}
              <div className="space-y-4">
                <div
                  className={`p-3 rounded-lg flex justify-between items-center ${formData.isSudden ? "bg-orange-50" : "bg-[#16a34a]/10"
                    }`}
                >
                  <h3
                    className={`text-sm font-medium uppercase tracking-widest ${formData.isSudden ? "text-orange-800" : "text-[#1e3a8a]"
                      }`}
                  >
                    {formData.isSudden
                      ? "Items to Deduct from Stock"
                      : "Production Items"}
                  </h3>
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className={`${formData.isSudden
                      ? "text-orange-600 hover:text-orange-700"
                      : "text-[#16a34a] hover:text-green-700"
                      } font-medium text-xs flex items-center space-x-1`}
                  >
                    <SafeIcon icon={FiPlusCircle} />
                    <span>Add Item</span>
                  </button>
                </div>

                <div className="space-y-3">
                  {formData.items.map((item, index) => {
                    const stock = calculateStock(item.varietyId);
                    const isStockLow =
                      formData.isSudden && stock < item.quantity;

                    return (
                      <div
                        key={index}
                        className="grid grid-cols-12 gap-3 items-start bg-gray-50 p-4 rounded-xl border border-gray-100"
                      >
                        <div className="col-span-12 md:col-span-6">
                          <label className="block text-[10px] font-medium text-gray-400 uppercase tracking-widest mb-1">
                            Variety
                          </label>
                          <VarietySelector
                            varieties={varieties}
                            value={item.varietyId}
                            onChange={(val) =>
                              handleItemChange(index, "varietyId", val)
                            }
                          />

                          {/* Price & Size Info */}
                          {item.varietyId &&
                            (() => {
                              const v = varieties.find(
                                (v) =>
                                  v.id.toString() === item.varietyId.toString()
                              );
                              if (!v) return null;
                              const price =
                                formData.priceType === "Supplier"
                                  ? v.supplierPrice
                                  : v.price;
                              return (
                                <div className="flex items-center space-x-2 mt-2">
                                  {price > 0 && (
                                    <span
                                      className={`text-[10px] font-bold px-2 py-1 rounded-full border ${formData.priceType === "Supplier"
                                        ? "text-[#1e3a8a] bg-blue-50 border-blue-100"
                                        : "text-[#16a34a] bg-green-50 border-green-100"
                                        }`}
                                    >
                                      RM {Number(price).toFixed(2)}
                                    </span>
                                  )}
                                  {v.size && (
                                    <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full border border-gray-200">
                                      {v.size}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}

                          {formData.isSudden && item.varietyId && (
                            <div
                              className={`text-[10px] mt-1 font-medium ${stock > 0 ? "text-green-600" : "text-red-500"
                                } pl-1`}
                            >
                              Available Stock: {stock} units
                            </div>
                          )}

                          {item.varietyId &&
                            (() => {
                              // Loose comparison
                              const v = varieties.find(
                                (v) =>
                                  v.id.toString() === item.varietyId.toString()
                              );
                              if (!v || !v.description) return null;
                              return (
                                <div className="mt-2 text-xs text-gray-500 font-light bg-blue-50/50 p-2 rounded-lg border border-blue-100 leading-relaxed">
                                  <span className="font-medium text-blue-800 mr-1">
                                    Note:
                                  </span>
                                  {v.description}
                                </div>
                              );
                            })()}
                        </div>
                        <div className="col-span-5 md:col-span-2">
                          <label className="block text-[10px] font-medium text-gray-400 uppercase tracking-widest mb-1">
                            Order Qty {varieties.find(v => v.id == item.varietyId)?.category === 'Edible Flowers' ? '(g)' : ''}
                          </label>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "quantity",
                                e.target.value
                              )
                            }
                            className={`w-full px-3 py-2 border rounded-lg outline-none bg-white text-sm font-medium text-center ${isStockLow
                              ? "border-red-300 ring-2 ring-red-100"
                              : "border-gray-200"
                              }`}
                            min="1"
                            required
                          />
                          {isStockLow && (
                            <div className="text-[9px] text-red-500 mt-1 font-bold">
                              Exceeds Avail.
                            </div>
                          )}
                        </div>

                        {!formData.isSudden && (
                          <div className="col-span-5 md:col-span-2">
                            <label className="block text-[10px] font-medium text-orange-400 uppercase tracking-widest mb-1">
                              Buffer Qty
                            </label>
                            <input
                              type="number"
                              value={item.bufferQuantity}
                              onChange={(e) =>
                                handleItemChange(
                                  index,
                                  "bufferQuantity",
                                  e.target.value
                                )
                              }
                              className="w-full px-3 py-2 border border-orange-100 rounded-lg outline-none bg-white text-sm font-medium text-center text-orange-600"
                              min="0"
                            />
                          </div>
                        )}

                        <div className="col-span-2 md:col-span-1 flex flex-col justify-center items-center space-y-1 pt-4">
                          <button
                            type="button"
                            onClick={() => handleMoveItem(index, "up")}
                            disabled={index === 0}
                            className={`p-1 rounded hover:bg-gray-100 ${index === 0
                              ? "text-gray-300"
                              : "text-gray-500 hover:text-blue-600"
                              }`}
                          >
                            <SafeIcon icon={FiIcons.FiArrowUp} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className="text-red-400 hover:text-red-600 transition-colors p-1"
                          >
                            <SafeIcon icon={FiMinusCircle} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMoveItem(index, "down")}
                            disabled={index === formData.items.length - 1}
                            className={`p-1 rounded hover:bg-gray-100 ${index === formData.items.length - 1
                              ? "text-gray-300"
                              : "text-gray-500 hover:text-blue-600"
                              }`}
                          >
                            <SafeIcon icon={FiIcons.FiArrowDown} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Schedule Preview */}
              <div
                className={`${formData.isSudden
                  ? "bg-orange-50 border-orange-100"
                  : "bg-blue-50 border-blue-100"
                  } p-6 rounded-2xl border`}
              >
                <h4
                  className={`text-xs font-bold uppercase tracking-widest mb-4 flex items-center space-x-2 ${formData.isSudden ? "text-orange-800" : "text-[#1e3a8a]"
                    }`}
                >
                  <SafeIcon icon={FiActivity} />
                  <span>
                    {formData.isSudden
                      ? "Stock Deduction Summary"
                      : "Production Schedule"}
                  </span>
                </h4>
                <div className="space-y-3">
                  {formData.items.map((item, idx) => {
                    const calc = calculateItemDates(
                      item.varietyId,
                      formData.baseDate,
                      formData.dateType,
                      formData.isSudden
                    );
                    if (!calc) return null;
                    const totalProd =
                      parseInt(item.quantity || 0) +
                      parseInt(item.bufferQuantity || 0);

                    // Find Variety Price
                    const v = varieties.find(
                      (v) => v.id.toString() === item.varietyId.toString()
                    );
                    const price = v
                      ? parseFloat(
                        (formData.priceType === "Supplier"
                          ? v.supplierPrice
                          : v.price) || 0
                      )
                      : 0;
                    const lineTotal = price * parseInt(item.quantity || 0);

                    if (formData.isSudden) {
                      return (
                        <div
                          key={idx}
                          className="flex justify-between items-center text-sm border-b border-orange-100/50 pb-2 last:border-0 last:pb-0"
                        >
                          <span className="text-gray-600">
                            {calc.variety?.name}
                          </span>
                          <div className="flex items-center space-x-4">
                            <span className="font-bold text-gray-900">
                              -{item.quantity} units
                            </span>
                            <span className="text-xs font-medium text-[#1e3a8a] min-w-[60px] text-right">
                              RM {lineTotal.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={idx}
                        className="bg-white p-3 rounded-xl border border-blue-100/50 shadow-sm flex justify-between items-center"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-900">
                              {calc.variety?.name}
                            </span>
                            <span className="text-[10px] text-gray-400">
                              Yield: {format(calc.harvestDate, "MMM dd")}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <div className="text-sm font-bold text-[#1e3a8a]">
                              {totalProd} units
                            </div>
                            <div className="text-[10px] text-gray-400">
                              Total Production
                            </div>
                          </div>
                          <span className="text-xs font-medium text-[#1e3a8a] min-w-[60px] text-right">
                            RM {lineTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Grand Total Preview */}
                <div className="mt-6 pt-4 border-t border-dashed border-gray-300 flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-500 uppercase tracking-widest">
                    Estimated Total
                  </span>
                  <span className="text-xl font-light text-[#1e3a8a]">
                    RM{" "}
                    {(() => {
                      const itemsTotal = formData.items.reduce((sum, item) => {
                        const v = varieties.find(
                          (v) => v.id.toString() === item.varietyId.toString()
                        );
                        const price = v
                          ? parseFloat(
                            (formData.priceType === "Supplier"
                              ? v.supplierPrice
                              : v.price) || 0
                          )
                          : 0;
                        return sum + price * parseInt(item.quantity || 0);
                      }, 0);
                      const fee = parseFloat(formData.deliveryFee || 0);
                      return (itemsTotal + fee).toFixed(2);
                    })()}
                  </span>
                </div>
              </div>

              {/* Status and Total */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice Sync Status
                  </label>
                  <div className="flex space-x-2">
                    {["No sync", "Ready to sync", "Done Sync"].map((status) => (
                      <button
                        key={status}
                        type="button"
                        onClick={() =>
                          setFormData((p) => ({ ...p, syncStatus: status }))
                        }
                        className={`px-3 py-2 text-xs font-medium rounded-lg border transition-all ${formData.syncStatus === status
                          ? status === "Done Sync"
                            ? "bg-green-50 text-green-700 border-green-200 ring-1 ring-green-200"
                            : status === "Ready to sync"
                              ? "bg-blue-50 text-blue-700 border-blue-200 ring-1 ring-blue-200"
                              : "bg-gray-100 text-gray-700 border-gray-300 ring-1 ring-gray-300"
                          : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                          }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-end justify-center">
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-1">
                    Estimated Total
                  </span>
                  <span className="text-2xl font-light text-[#1e3a8a]">
                    {(() => {
                      const total = formData.items.reduce((sum, item) => {
                        // Find variety to get price
                        const v = varieties.find(
                          (v) => v.id.toString() === item.varietyId
                        );
                        const price = v
                          ? parseFloat(
                            (formData.priceType === "Supplier"
                              ? v.supplierPrice
                              : v.price) || 0
                          )
                          : 0;

                        // Strict: Use ONLY item.quantity (Order Qty), IGNORE bufferQuantity
                        const qty = parseFloat(item.quantity || 0);

                        return sum + price * qty;
                      }, 0);
                      const startTotal = total + parseFloat(formData.deliveryFee || 0);
                      return `RM ${startTotal.toFixed(2)}`;
                    })()}
                  </span>
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-[#16a34a] text-white py-4 rounded-xl font-medium hover:bg-green-700 transition-all shadow-lg shadow-green-100 disabled:opacity-50"
                >
                  {isLoading
                    ? "Saving..."
                    : editingOrder
                      ? "Update Order"
                      : "Confirm Order"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-8 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManager;
